import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Database, Download, RefreshCw, Play, AlertCircle, CheckCircle, Clock, TrendingUp, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import sampleDataService from '../../services/sampleDataService';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

export default function SampleDataManagementDashboard() {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entityStats, setEntityStats] = useState([]);
  const [operations, setOperations] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [selectedNiche, setSelectedNiche] = useState('hvac');

  const niches = [
    { value: 'hvac', label: 'HVAC Services', icon: '❄️' },
    { value: 'plumbing', label: 'Plumbing Services', icon: '🔧' },
    { value: 'remodeling', label: 'Remodeling & Renovation', icon: '🏗️' },
    { value: 'handyman', label: 'Handyman Services', icon: '🛠️' },
    { value: 'electrical', label: 'Electrical Services', icon: '⚡' },
    { value: 'landscaping', label: 'Landscaping', icon: '🌳' },
    { value: 'cleaning', label: 'Cleaning Services', icon: '🧹' },
    { value: 'painting', label: 'Painting Services', icon: '🎨' }
  ];

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadDashboardData();
  }, [isSuperAdmin]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch both statistics AND actual sample data records from database
      const entities = ['contacts', 'deals', 'customers', 'activities', 'services', 'organizations', 'appointments', 'invoices'];
      const statsPromises = entities?.map(async (entity) => {
        // Get statistics from sample_data_sets
        const { data: setData } = await supabase?.from('sample_data_sets')?.select('id, record_count, quality_score, last_generated_at')?.eq('entity_type', entity)?.order('created_at', { ascending: false })?.limit(1)?.single();

        // Get actual record count from sample_data_records table
        let actualRecordCount = 0;
        if (setData?.id) {
          const { count } = await supabase?.from('sample_data_records')?.select('id', { count: 'exact', head: true })?.eq('set_id', setData?.id);
          actualRecordCount = count || 0;
        }

        return {
          entityType: entity,
          recordCount: actualRecordCount, // Use actual count from records table
          qualityScore: setData?.quality_score || 0,
          lastGeneratedAt: setData?.last_generated_at || null,
          hasData: actualRecordCount > 0
        };
      });

      const [stats, ops] = await Promise.all([
        Promise.all(statsPromises),
        sampleDataService?.getOperations(null, 20)
      ]);
      
      setEntityStats(stats || []);
      setOperations(ops || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAll = () => {
    setConfirmAction({ type: 'generate_all', title: 'Generate All Sample Data', niche: selectedNiche });
    setShowConfirmDialog(true);
  };

  const handleResetAll = () => {
    setConfirmAction({ type: 'reset_all', title: 'Reset All Sample Data' });
    setShowConfirmDialog(true);
  };

  const handleClearAll = () => {
    setConfirmAction({ 
      type: 'clear_all', 
      title: 'Clear All Sample Data',
      message: 'This will permanently delete ALL sample data across all entities. This is useful when switching to a new niche or starting fresh. This action cannot be undone.'
    });
    setShowConfirmDialog(true);
  };

  const handleExportAll = async () => {
    try {
      setProcessing(true);
      await sampleDataService?.exportSampleData(null);
      await loadDashboardData();
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleEntityAction = async (entityType, action) => {
    try {
      setProcessing(true);
      if (action === 'generate') {
        await sampleDataService?.generateSampleData(entityType, { count: 50, niche: selectedNiche });
      } else if (action === 'reset') {
        await sampleDataService?.resetSampleData(entityType);
      } else if (action === 'export') {
        await sampleDataService?.exportSampleData(entityType);
      }
      await loadDashboardData();
    } catch (error) {
      console.error('Entity action error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const confirmDialogAction = async () => {
    try {
      setProcessing(true);
      if (confirmAction?.type === 'generate_all') {
        await sampleDataService?.bulkGenerateAll({ count: 50, niche: confirmAction?.niche });
      } else if (confirmAction?.type === 'reset_all') {
        for (const stat of entityStats) {
          await sampleDataService?.resetSampleData(stat?.entityType);
        }
      } else if (confirmAction?.type === 'clear_all') {
        await sampleDataService?.clearAllSampleData();
      }
      await loadDashboardData();
      setShowConfirmDialog(false);
    } catch (error) {
      console.error('Confirm action error:', error);
    } finally {
      setProcessing(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only Super Admins can access Sample Data Management.</p>
        </div>
      </div>
    );
  }

  const entityIcons = {
    contacts: 'Users',
    deals: 'TrendingUp',
    customers: 'Building2',
    activities: 'Activity',
    services: 'Briefcase',
    organizations: 'Building',
    appointments: 'Calendar',
    invoices: 'FileText'
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'generating': return 'text-blue-600 bg-blue-50';
      case 'failed': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <>
      <Helmet>
        <title>Sample Data Management - CRM</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Database className="w-8 h-8 text-blue-600" />
                Sample Data Management
              </h1>
              <p className="text-gray-600 mt-1">Central control for sample data across all CRM entities</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleExportAll}
                disabled={processing}
                variant="outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Export All
              </Button>
              <Button
                onClick={handleClearAll}
                disabled={processing}
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Data
              </Button>
              <Button
                onClick={handleResetAll}
                disabled={processing}
                variant="outline"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset All
              </Button>
              <Button
                onClick={handleGenerateAll}
                disabled={processing}
              >
                <Play className="w-4 h-4 mr-2" />
                Generate All
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Niche Selector */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-6 mb-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    Industry Niche Selector
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Select your business niche to generate contextually relevant sample data for contacts, services, invoices, and appointments.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                    {niches?.map((niche) => (
                      <button
                        key={niche?.value}
                        onClick={() => setSelectedNiche(niche?.value)}
                        className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                          selectedNiche === niche?.value
                            ? 'border-blue-600 bg-blue-100 shadow-md'
                            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <span className="text-3xl mb-2">{niche?.icon}</span>
                        <span className={`text-xs font-medium text-center ${
                          selectedNiche === niche?.value ? 'text-blue-900' : 'text-gray-700'
                        }`}>
                          {niche?.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-100 rounded-lg border border-blue-300">
                <p className="text-sm text-blue-900">
                  <strong>Selected:</strong> {niches?.find(n => n?.value === selectedNiche)?.label} - 
                  All generated data will reflect this industry's typical services, terminology, and business patterns.
                </p>
              </div>
            </div>

            {/* Entity Overview Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {entityStats?.map((stat) => (
                <div key={stat?.entityType} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Icon name={entityIcons?.[stat?.entityType] || 'Database'} className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 capitalize">{stat?.entityType}</h3>
                        <p className="text-sm text-gray-500">{stat?.recordCount} records</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Quality Score</span>
                      <span className="font-semibold text-gray-900">{stat?.qualityScore}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${stat?.qualityScore}%` }}
                      ></div>
                    </div>
                  </div>

                  {stat?.lastGeneratedAt && (
                    <p className="text-xs text-gray-500 mb-4">
                      Last generated: {new Date(stat?.lastGeneratedAt)?.toLocaleDateString()}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleEntityAction(stat?.entityType, 'generate')}
                      disabled={processing}
                      className="flex-1"
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Generate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEntityAction(stat?.entityType, 'export')}
                      disabled={processing}
                    >
                      <Download className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEntityAction(stat?.entityType, 'reset')}
                      disabled={processing}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Operations */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-600" />
                Recent Operations
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operation</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Records</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performed By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {operations?.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          No operations recorded yet
                        </td>
                      </tr>
                    ) : (
                      operations?.map((op) => (
                        <tr key={op?.id}>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900 capitalize">{op?.operationType?.replace('_', ' ')}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600 capitalize">{op?.entityType || 'All'}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">{op?.recordsAffected || 0}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(op?.status)}`}>
                              {op?.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                              {op?.status === 'failed' && <AlertCircle className="w-3 h-3 mr-1" />}
                              {op?.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              {op?.performedBy?.first_name} {op?.performedBy?.last_name}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-500">
                              {new Date(op?.startedAt)?.toLocaleString()}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-start gap-3 mb-4">
                {confirmAction?.type === 'clear_all' ? (
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-blue-600" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{confirmAction?.title}</h3>
                  <p className="text-gray-600 text-sm">
                    {confirmAction?.message || 'Are you sure you want to proceed? This action will affect all CRM entities.'}
                  </p>
                  {confirmAction?.type === 'clear_all' && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-yellow-800 font-medium">
                        ⚠️ Warning: This will delete all sample data records and reset all counts to zero. Perfect for switching niches!
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmDialog(false)}
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDialogAction}
                  disabled={processing}
                  className={confirmAction?.type === 'clear_all' ? 'bg-red-600 hover:bg-red-700' : ''}
                >
                  {processing ? 'Processing...' : 'Confirm'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
